// src/components/CashUpiBox.js — Dashboard: cash and UPI payments confirmed by drivers.
// Today (default) comes from the dashboard summary and refreshes with it. Any other date or range is fetched from
// GET /api/dashboard/cash-upi. Print gives an A4 sheet with the Cash list, the UPI list and the totals.
import React, { useState, useEffect } from 'react';
import { get, getUser } from '../utils/api';
import { formatCurrency } from './Shared';

const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });   // YYYY-MM-DD
const dmy = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? m[3] + '-' + m[2] + '-' + m[1] : (s || ''); };

function payClock(ts) {
  try {
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  } catch (e) { return ''; }
}

export default function CashUpiBox({ data }) {
  const [tab, setTab] = useState('cash');
  const [from, setFrom] = useState('');     // '' = today
  const [to, setTo] = useState('');
  const [extra, setExtra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const today = todayIST();
  const f = from || today;
  const t = to || today;
  const isToday = !from && !to;
  const multi = f !== t;

  useEffect(() => {
    if (!from && !to) { setExtra(null); setErr(''); return undefined; }
    let stale = false;
    setLoading(true); setErr('');
    get('/api/dashboard/cash-upi?from=' + encodeURIComponent(from || to) + '&to=' + encodeURIComponent(to || from))
      .then(d => { if (!stale) setExtra(d); })
      .catch(e => { if (!stale) { setExtra(null); setErr(e.message || 'Could not load'); } })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [from, to]);

  const shown = isToday ? data : extra;
  if (isToday && !data) return null;

  const cash = (shown && shown.cash) || [];
  const upi = (shown && shown.upi) || [];
  const cashTotal = (shown && shown.cash_total) || 0;
  const upiTotal = (shown && shown.upi_total) || 0;
  const rows = tab === 'cash' ? cash : upi;
  const total = tab === 'cash' ? cashTotal : upiTotal;
  const when = isToday ? 'today' : (multi ? dmy(f) + ' to ' + dmy(t) : dmy(f));

  function printSheet() {
    const esc = s => String(s === null || s === undefined ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const table = (label, list, sum) => {
      const heads = ['#'].concat(multi ? ['Date'] : [], ['Invoice', 'Party', 'Driver', 'Time', 'Amount']);
      const body = list.map((r, i) => '<tr>' + [i + 1].concat(multi ? [dmy(r.pay_date)] : [], [
        r.invoice_number, r.party_name, r.driver || '-', payClock(r.confirmed_at), formatCurrency(r.invoice_value),
      ]).map((c, k, a) => '<td' + (k === a.length - 1 ? ' class="r"' : '') + '>' + esc(c) + '</td>').join('') + '</tr>').join('');
      return '<h2>' + label + ' (' + list.length + ')</h2>' + (list.length === 0 ? '<p>None.</p>' :
        '<table><thead><tr>' + heads.map((h, k) => '<th' + (k === heads.length - 1 ? ' class="r"' : '') + '>' + h + '</th>').join('') + '</tr></thead><tbody>' + body +
        '</tbody><tfoot><tr><td colspan="' + (heads.length - 1) + '">Total ' + label.toLowerCase() + '</td><td class="r">' + esc(formatCurrency(sum)) + '</td></tr></tfoot></table>');
    };
    const user = (getUser() || {}).name || '';
    const html = '<!doctype html><html><head><meta charset="utf-8"><title>Cash and UPI</title><style>' +
      '@page{size:A4 portrait;margin:12mm}body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#000}' +
      'h1{font-size:17px;margin:0 0 2px}h2{font-size:13px;margin:14px 0 4px}p{margin:0 0 6px;font-size:11px}' +
      'table{width:100%;border-collapse:collapse}th,td{border:1px solid #444;padding:4px 5px;vertical-align:top}th{background:#eee;text-align:left}' +
      'td.r,th.r{text-align:right}tfoot td{font-weight:bold}tr{page-break-inside:avoid}thead{display:table-header-group}' +
      '.g{margin-top:12px;font-size:13px;font-weight:bold}</style></head><body><h1>Cash and UPI collections</h1><p>' +
      (multi ? esc(dmy(f)) + ' to ' + esc(dmy(t)) : esc(dmy(f))) + ' &nbsp;|&nbsp; Payments confirmed by drivers &nbsp;|&nbsp; Printed ' +
      esc(new Date().toLocaleString('en-IN')) + (user ? ' by ' + esc(user) : '') + '</p>' +
      table('Cash', cash, cashTotal) + table('UPI', upi, upiTotal) +
      '<p class="g">Grand total (cash + UPI): ' + esc(formatCurrency(cashTotal + upiTotal)) + '</p></body></html>';
    const w = window.open('', '_blank');
    if (!w) { setErr('The print window was blocked. Allow pop-ups for this site and try again.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  }

  const tabBtn = (key, label, count, sum) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      className={'px-4 py-2 rounded-lg text-sm font-semibold border ' + (tab === key
        ? 'bg-brand-700 text-white border-brand-700'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600')}
    >
      {label} ({count}) · {formatCurrency(sum)}
    </button>
  );
  const inp = 'border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-sm';

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Cash and UPI {isToday ? 'today' : ''}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">Payments confirmed by drivers {isToday ? 'today' : (multi ? dmy(f) + ' to ' + dmy(t) : 'on ' + dmy(f))}</span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col gap-1">From
          <input type="date" className={inp} value={f} max={today} onChange={e => setFrom(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400 flex flex-col gap-1">To
          <input type="date" className={inp} value={t} max={today} onChange={e => setTo(e.target.value)} />
        </label>
        {!isToday && (
          <button onClick={() => { setFrom(''); setTo(''); }} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm">Today</button>
        )}
        <button onClick={printSheet} disabled={loading || cash.length + upi.length === 0}
          className="px-3 py-1.5 bg-brand-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">Print</button>
        {loading && <span className="text-sm text-gray-500">Loading…</span>}
        {err && <span className="text-sm text-red-600">{err}</span>}
      </div>

      <div className="flex flex-wrap gap-2">
        {tabBtn('cash', 'Cash', cash.length, cashTotal)}
        {tabBtn('upi', 'UPI', upi.length, upiTotal)}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
          {loading ? 'Loading…' : (tab === 'cash' ? 'No cash payments ' : 'No UPI payments ') + (isToday ? 'today.' : 'in this period.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-2 pr-3 font-semibold">Invoice</th>
              <th className="py-2 px-3 font-semibold">Party</th>
              <th className="py-2 px-3 font-semibold">Driver</th>
              {multi && <th className="py-2 px-3 font-semibold">Date</th>}
              <th className="py-2 px-3 font-semibold">Time</th>
              <th className="py-2 pl-3 font-semibold text-right">Amount</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.voucher_id} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-2.5 pr-3 font-semibold text-brand-700 dark:text-brand-500 tabular-nums">{r.invoice_number}</td>
                  <td className="py-2.5 px-3">{r.party_name}</td>
                  <td className="py-2.5 px-3">{r.driver || '-'}</td>
                  {multi && <td className="py-2.5 px-3 tabular-nums">{dmy(r.pay_date)}</td>}
                  <td className="py-2.5 px-3 tabular-nums">{payClock(r.confirmed_at)}</td>
                  <td className="py-2.5 pl-3 text-right tabular-nums">{formatCurrency(r.invoice_value)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                <td className="py-2.5 pr-3" colSpan={multi ? 5 : 4}>Total {tab === 'cash' ? 'cash' : 'UPI'}</td>
                <td className="py-2.5 pl-3 text-right tabular-nums">{formatCurrency(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
